import { useCallback, useEffect, useState } from "react";
import {
  PixelConfigResponse,
  CreatePixelConfigRequest,
  UpdatePixelConfigRequest,
} from "@codway-dev/shared";
import { PixelService } from "../services/pixel.service";

export interface UsePixelsResult {
  pixels: PixelConfigResponse[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createPixel: (body: CreatePixelConfigRequest) => Promise<void>;
  /**
   * Updates a pixel. Soft delete is an update with is_active: false — when that
   * comes back the pixel drops out of the (active-only) list automatically.
   */
  updatePixel: (id: string, body: UpdatePixelConfigRequest) => Promise<void>;
}

export function usePixels(): UsePixelsResult {
  const [pixels, setPixels] = useState<PixelConfigResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { items } = await PixelService.list();
      setPixels(items);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "No se pudieron cargar los píxeles"
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  // create/update intentionally do NOT swallow errors — the form awaits them and
  // surfaces the message inline. State updates only after a successful call, at
  // which point we also clear any stale list-level error (e.g. a failed initial
  // refetch) so it can't linger over a now-successful screen.
  const createPixel = useCallback(async (body: CreatePixelConfigRequest) => {
    const created = await PixelService.create(body);
    setPixels((prev) => [...prev, created]);
    setError(null);
  }, []);

  const updatePixel = useCallback(
    async (id: string, body: UpdatePixelConfigRequest) => {
      const updated = await PixelService.update(id, body);
      setPixels((prev) =>
        updated.is_active
          ? prev.map((p) => (p.id === id ? updated : p))
          : prev.filter((p) => p.id !== id)
      );
      setError(null);
    },
    []
  );

  return { pixels, isLoading, error, refetch, createPixel, updatePixel };
}
